import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Mail, RefreshCw, Send, X, Facebook, Instagram, Twitter, Linkedin, Youtube, Phone, Edit, UserPlus, Trash2, Copy } from 'lucide-react';

const SOCIAL_FIELDS = [
  { key: 'facebook', icon: Facebook, label: 'Facebook', placeholder: 'https://facebook.com/...' },
  { key: 'instagram', icon: Instagram, label: 'Instagram', placeholder: 'https://instagram.com/...' },
  { key: 'twitter', icon: Twitter, label: 'X (Twitter)', placeholder: 'https://x.com/...' },
  { key: 'linkedin', icon: Linkedin, label: 'LinkedIn', placeholder: 'https://linkedin.com/company/...' },
  { key: 'youtube', icon: Youtube, label: 'YouTube', placeholder: 'https://youtube.com/@...' },
  { key: 'whatsapp', icon: Phone, label: 'WhatsApp', placeholder: 'https://wa.me/...' },
];

const buildSocialIconsHtml = (links) => {
  if (!links) return '';
  const iconMap = {
    facebook: { url: links.facebook, color: '#1877F2', label: 'Facebook' },
    instagram: { url: links.instagram, color: '#E4405F', label: 'Instagram' },
    twitter: { url: links.twitter, color: '#000000', label: 'X' },
    linkedin: { url: links.linkedin, color: '#0A66C2', label: 'LinkedIn' },
    youtube: { url: links.youtube, color: '#FF0000', label: 'YouTube' },
    whatsapp: { url: links.whatsapp, color: '#25D366', label: 'WhatsApp' },
  };
  const activeLinks = Object.entries(iconMap).filter(([, v]) => v.url);
  if (activeLinks.length === 0) return '';
  return `<table cellpadding="0" cellspacing="0" style="margin-top:10px;"><tr>${activeLinks.map(([, v]) =>
    `<td style="padding-right:12px;"><a href="${v.url}" style="color:${v.color};text-decoration:none;font-family:Arial,sans-serif;font-size:12px;font-weight:bold;">${v.label}</a></td>`
  ).join('')}</tr></table>`;
};

const SocialIconsPreview = ({ links }) => {
  if (!links) return null;
  const active = SOCIAL_FIELDS.filter(s => links[s.key]);
  if (active.length === 0) return null;
  return (
    <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
      {active.map(s => (
        <a key={s.key} href={links[s.key]} target="_blank" rel="noopener noreferrer"
          style={{ color: '#D4AF37', textDecoration: 'none', fontSize: '11px', fontWeight: 'bold' }}
        >{s.label}</a>
      ))}
    </div>
  );
};

const EmailSignaturesTab = ({ authAxios }) => {
  const [emailSignatures, setEmailSignatures] = useState({
    company_signature: {
      tagline: '', phone: '', mobile: '', email: '', website: '',
      address_line1: '', address_line2: '', logo_url: '', awards: [],
      social_links: { facebook: '', instagram: '', twitter: '', linkedin: '', youtube: '', whatsapp: '' }
    },
    team_signatures: []
  });
  const [editingCompany, setEditingCompany] = useState(false);
  const [editingTeamSig, setEditingTeamSig] = useState(null);
  const [showAddTeamSig, setShowAddTeamSig] = useState(false);
  const [newTeamSig, setNewTeamSig] = useState({
    name: '', title: '', phone: '', mobile: '', email: '', linkedin_url: '', photo_url: ''
  });
  const [copiedSig, setCopiedSig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sendTestModal, setSendTestModal] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchEmailSignatures();
  }, [authAxios]);

  const fetchEmailSignatures = async () => {
    try {
      const response = await authAxios.get('/admin/email-signatures');
      const data = response.data;
      if (!data.company_signature.social_links) {
        data.company_signature.social_links = { facebook: '', instagram: '', twitter: '', linkedin: '', youtube: '', whatsapp: '' };
      }
      setEmailSignatures(data);
    } catch (error) {
      console.error('Failed to fetch email signatures:', error);
    }
  };

  const handleSyncFromTeam = async () => {
    setSyncing(true);
    try {
      await authAxios.post('/admin/email-signatures/sync-team');
      await authAxios.post('/admin/email-signatures/sync-company');
      toast.success('Signatures synced from team & settings');
      fetchEmailSignatures();
    } catch (error) {
      toast.error('Failed to sync');
    } finally {
      setSyncing(false);
    }
  };

  const buildCompanySignatureHtml = (s) => {
    const socialHtml = buildSocialIconsHtml(s.social_links);
    const awardsHtml = s.awards?.length > 0
      ? `<tr><td colspan="2" style="padding-top:12px;"><table cellpadding="0" cellspacing="0"><tr>${s.awards.map(a => `<td style="padding-right:8px;"><img src="${a.image_url}" alt="${a.name}" width="40" style="height:40px;width:auto;display:block;" /></td>`).join('')}</tr></table></td></tr>`
      : '';
    return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#333333;line-height:1.4;"><tr><td style="padding-right:20px;vertical-align:top;border-right:2px solid #D4AF37;"><img src="${s.logo_url}" alt="Aureate Residences" width="80" style="width:80px;height:auto;display:block;" /></td><td style="padding-left:20px;vertical-align:top;"><table cellpadding="0" cellspacing="0" border="0"><tr><td style="font-weight:bold;font-size:16px;color:#1a1a1a;padding-bottom:2px;">Aureate Residences</td></tr><tr><td style="color:#D4AF37;font-size:12px;padding-bottom:10px;">${s.tagline}</td></tr><tr><td style="font-size:13px;padding-bottom:3px;">Tel: ${s.phone}${s.mobile ? ` | Mob: ${s.mobile}` : ''}</td></tr><tr><td style="font-size:13px;padding-bottom:3px;">Email: <a href="mailto:${s.email}" style="color:#333333;text-decoration:none;">${s.email}</a></td></tr><tr><td style="font-size:13px;padding-bottom:3px;">Web: <a href="https://${s.website}" style="color:#D4AF37;text-decoration:none;">${s.website}</a></td></tr><tr><td style="font-size:12px;color:#666666;padding-top:8px;">${s.address_line1}<br/>${s.address_line2}</td></tr>${awardsHtml}<tr><td>${socialHtml}</td></tr></table></td></tr></table>`;
  };

  const buildTeamSignatureHtml = (ts, compSig) => {
    const socialHtml = buildSocialIconsHtml(compSig.social_links);
    const awardsHtml = compSig.awards?.length > 0
      ? `<tr><td style="padding-top:8px;"><table cellpadding="0" cellspacing="0"><tr>${compSig.awards.map(a => `<td style="padding-right:6px;"><img src="${a.image_url}" alt="${a.name}" width="35" style="height:35px;width:auto;display:block;" /></td>`).join('')}</tr></table></td></tr>`
      : '';
    const photoHtml = ts.photo_url
      ? `<img src="${ts.photo_url}" alt="${ts.name}" width="70" height="70" style="width:70px;height:70px;border-radius:50%;object-fit:cover;display:block;" />`
      : `<img src="${compSig.logo_url}" alt="Aureate Residences" width="70" style="width:70px;height:auto;display:block;" />`;
    return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#333333;line-height:1.4;"><tr><td style="padding-right:20px;vertical-align:top;border-right:2px solid #D4AF37;">${photoHtml}</td><td style="padding-left:20px;vertical-align:top;"><table cellpadding="0" cellspacing="0" border="0"><tr><td style="font-weight:bold;font-size:16px;color:#1a1a1a;padding-bottom:2px;">${ts.name}</td></tr><tr><td style="color:#D4AF37;font-size:13px;padding-bottom:8px;">${ts.title}</td></tr><tr><td style="font-size:13px;padding-bottom:3px;">Tel: ${ts.phone || compSig.phone}</td></tr><tr><td style="font-size:13px;padding-bottom:3px;">Mob: ${ts.mobile || compSig.mobile}</td></tr><tr><td style="font-size:13px;padding-bottom:3px;">Email: <a href="mailto:${ts.email}" style="color:#333333;text-decoration:none;">${ts.email}</a></td></tr>${ts.linkedin_url ? `<tr><td style="font-size:13px;padding-bottom:3px;"><a href="${ts.linkedin_url}" style="color:#0077B5;text-decoration:none;">LinkedIn</a></td></tr>` : ''}<tr><td style="font-size:12px;color:#666666;padding-top:8px;border-top:1px solid #eeeeee;"><strong style="color:#1a1a1a;">Aureate Residences</strong> | <a href="https://${compSig.website}" style="color:#D4AF37;text-decoration:none;font-size:12px;">${compSig.website}</a><br/>${compSig.address_line1}, ${compSig.address_line2}</td></tr>${awardsHtml}<tr><td>${socialHtml}</td></tr></table></td></tr></table>`;
  };

  const handleCopy = async (html, id) => {
    try {
      const blob = new Blob([html], { type: 'text/html' });
      await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob })]);
    } catch {
      navigator.clipboard.writeText(html);
    }
    setCopiedSig(id);
    toast.success('Signature copied — paste into your email client');
    setTimeout(() => setCopiedSig(null), 2000);
  };

  const handleSendTest = async () => {
    if (!testEmail || !sendTestModal) return;
    setSending(true);
    try {
      const response = await authAxios.post('/admin/email-signatures/send-test', {
        recipient_email: testEmail, signature_html: sendTestModal.sigHtml, signature_name: sendTestModal.sigName
      });
      if (response.data.success) {
        toast.success(`Test email sent to ${testEmail}`);
        setSendTestModal(null); setTestEmail('');
      } else toast.error(response.data.message);
    } catch { toast.error('Failed to send test email'); }
    finally { setSending(false); }
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      await authAxios.put('/admin/email-signatures/company', emailSignatures.company_signature);
      toast.success('Company signature saved!');
      setEditingCompany(false);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const sig = emailSignatures.company_signature;
  const socialLinks = sig.social_links || {};

  return (
    <div className="space-y-6" data-testid="email-signatures-tab">

      {/* ===== COMPANY SIGNATURE CARD ===== */}
      <div className="bg-[#111111] border border-white/5 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-mono font-bold text-lg">Company Signature</h2>
          <div className="flex gap-2">
            <button onClick={() => handleCopy(buildCompanySignatureHtml(sig), 'company')} data-testid="copy-company-signature"
              className={`px-3 py-1.5 font-mono text-xs flex items-center gap-1.5 transition-colors ${copiedSig === 'company' ? 'bg-green-500 text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}>
              <Copy className="w-3.5 h-3.5" />{copiedSig === 'company' ? 'Copied' : 'Copy'}
            </button>
            <button onClick={() => setSendTestModal({ sigName: 'Company', sigHtml: buildCompanySignatureHtml(sig) })} data-testid="send-test-company-signature"
              className="px-3 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] font-mono text-xs flex items-center gap-1.5 hover:bg-[#D4AF37]/10">
              <Send className="w-3.5 h-3.5" /> Test
            </button>
            <button onClick={() => setEditingCompany(true)} data-testid="edit-company-signature"
              className="px-3 py-1.5 bg-[#D4AF37] text-black font-mono text-xs font-bold flex items-center gap-1.5 hover:bg-[#D4AF37]/90">
              <Edit className="w-3.5 h-3.5" /> Edit
            </button>
          </div>
        </div>

        {/* Live Preview */}
        <div className="bg-white p-6 rounded">
          <table cellPadding="0" cellSpacing="0" style={{fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#333'}}>
            <tbody><tr>
              <td style={{paddingRight: '20px', verticalAlign: 'top', borderRight: '2px solid #D4AF37'}}>
                <img src={sig.logo_url} alt="Logo" style={{width: '80px', height: 'auto'}} />
              </td>
              <td style={{paddingLeft: '20px', verticalAlign: 'top'}}>
                <div style={{fontWeight: 'bold', fontSize: '16px', color: '#1a1a1a'}}>Aureate Residences</div>
                <div style={{color: '#D4AF37', fontSize: '12px', marginBottom: '10px'}}>{sig.tagline}</div>
                <div style={{fontSize: '13px', marginBottom: '3px'}}>Tel: {sig.phone}{sig.mobile && ` | Mob: ${sig.mobile}`}</div>
                <div style={{fontSize: '13px', marginBottom: '3px'}}>Email: {sig.email}</div>
                <div style={{fontSize: '13px', marginBottom: '3px'}}>Web: {sig.website}</div>
                <div style={{fontSize: '12px', color: '#666', marginTop: '8px'}}>{sig.address_line1}<br/>{sig.address_line2}</div>
                {sig.awards?.length > 0 && (
                  <div style={{marginTop: '12px', display: 'flex', gap: '8px'}}>
                    {sig.awards.map((a, i) => <img key={i} src={a.image_url} alt={a.name} style={{height: '40px', width: 'auto'}} />)}
                  </div>
                )}
                <SocialIconsPreview links={socialLinks} />
              </td>
            </tr></tbody>
          </table>
        </div>
      </div>

      {/* ===== TEAM SIGNATURES ===== */}
      <div className="bg-[#111111] border border-white/5 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-mono font-bold text-lg">Team Signatures</h2>
          <div className="flex gap-2">
            <button onClick={handleSyncFromTeam} disabled={syncing} data-testid="sync-team-signatures"
              className="px-3 py-1.5 border border-white/20 text-white/70 font-mono text-xs hover:bg-white/10 flex items-center gap-1.5 disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} /> Sync
            </button>
            <button onClick={() => setShowAddTeamSig(true)} data-testid="add-team-signature"
              className="px-3 py-1.5 bg-[#D4AF37] text-black font-mono text-xs font-bold flex items-center gap-1.5 hover:bg-[#D4AF37]/90">
              <UserPlus className="w-3.5 h-3.5" /> Add Member
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {emailSignatures.team_signatures?.map((ts, idx) => (
            <div key={ts.id || idx} className="bg-[#0A0A0A] border border-white/10 rounded overflow-hidden">
              {/* Card Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <span className="font-mono font-bold text-sm">{ts.name} <span className="text-white/40 font-normal">- {ts.title}</span></span>
                <div className="flex gap-2">
                  <button onClick={() => handleCopy(buildTeamSignatureHtml(ts, sig), ts.id)}
                    className={`px-2.5 py-1 font-mono text-xs flex items-center gap-1 transition-colors ${copiedSig === ts.id ? 'bg-green-500 text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                    <Copy className="w-3 h-3" />{copiedSig === ts.id ? 'Copied' : 'Copy'}
                  </button>
                  <button onClick={() => setSendTestModal({ sigName: ts.name, sigHtml: buildTeamSignatureHtml(ts, sig) })}
                    className="px-2.5 py-1 border border-[#D4AF37]/40 text-[#D4AF37] font-mono text-xs flex items-center gap-1 hover:bg-[#D4AF37]/10"
                    data-testid={`send-test-team-sig-${ts.id || idx}`}>
                    <Send className="w-3 h-3" /> Test
                  </button>
                  <button onClick={() => setEditingTeamSig({...ts})} data-testid={`edit-team-sig-${ts.id || idx}`}
                    className="px-2.5 py-1 bg-[#D4AF37] text-black font-mono text-xs font-bold flex items-center gap-1 hover:bg-[#D4AF37]/90">
                    <Edit className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={async () => {
                    if (window.confirm(`Delete signature for ${ts.name}?`)) {
                      try { await authAxios.delete(`/admin/email-signatures/team/${ts.id}`); fetchEmailSignatures(); toast.success('Deleted'); }
                      catch { toast.error('Failed'); }
                    }
                  }} className="px-2 py-1 text-red-400/60 hover:text-red-400 hover:bg-red-400/10 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {/* Card Preview */}
              <div className="bg-white p-4">
                <table cellPadding="0" cellSpacing="0" style={{fontFamily: 'Arial, sans-serif', fontSize: '13px', color: '#333'}}>
                  <tbody><tr>
                    <td style={{paddingRight: '15px', verticalAlign: 'top', borderRight: '2px solid #D4AF37'}}>
                      {ts.photo_url ? (
                        <img src={ts.photo_url} alt={ts.name} style={{width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover'}} />
                      ) : (
                        <img src={sig.logo_url} alt="Logo" style={{width: '60px', height: 'auto'}} />
                      )}
                    </td>
                    <td style={{paddingLeft: '15px', verticalAlign: 'top'}}>
                      <div style={{fontWeight: 'bold', fontSize: '14px', color: '#1a1a1a'}}>{ts.name}</div>
                      <div style={{color: '#D4AF37', fontSize: '12px', marginBottom: '6px'}}>{ts.title}</div>
                      <div style={{fontSize: '12px'}}>Tel: {ts.phone || sig.phone}</div>
                      <div style={{fontSize: '12px'}}>Mob: {ts.mobile || sig.mobile}</div>
                      <div style={{fontSize: '12px'}}>Email: {ts.email}</div>
                      {ts.linkedin_url && <div style={{fontSize: '12px'}}><a href={ts.linkedin_url} style={{color: '#0077B5'}}>LinkedIn</a></div>}
                      <div style={{fontSize: '11px', color: '#666', marginTop: '8px', borderTop: '1px solid #eee', paddingTop: '8px'}}>
                        <strong style={{color: '#1a1a1a'}}>Aureate Residences</strong> | <a href={`https://${sig.website}`} style={{color: '#D4AF37', textDecoration: 'none', fontSize: '11px'}}>{sig.website}</a><br/>
                        {sig.address_line1}, {sig.address_line2}
                      </div>
                      {sig.awards?.length > 0 && (
                        <div style={{marginTop: '8px', display: 'flex', gap: '6px'}}>
                          {sig.awards.map((a, i) => <img key={i} src={a.image_url} alt={a.name} style={{height: '30px', width: 'auto'}} />)}
                        </div>
                      )}
                      <SocialIconsPreview links={socialLinks} />
                    </td>
                  </tr></tbody>
                </table>
              </div>
            </div>
          ))}

          {(!emailSignatures.team_signatures || emailSignatures.team_signatures.length === 0) && (
            <div className="text-center py-8 text-white/30 font-mono text-sm">
              No team signatures. Click "Add Member" or "Sync" to create them.
            </div>
          )}
        </div>
      </div>

      {/* ===== EDIT COMPANY SIGNATURE MODAL ===== */}
      {editingCompany && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111111] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 sticky top-0 bg-[#111111] z-10">
              <h3 className="font-mono font-bold text-lg">Edit Company Signature</h3>
              <button onClick={() => setEditingCompany(false)} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: 'tagline', label: 'Tagline' }, { key: 'logo_url', label: 'Logo URL' },
                  { key: 'phone', label: 'Office Phone' }, { key: 'mobile', label: 'Mobile' },
                  { key: 'email', label: 'Email' }, { key: 'website', label: 'Website' },
                  { key: 'address_line1', label: 'Address Line 1' }, { key: 'address_line2', label: 'Address Line 2' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="text-white/50 text-xs font-mono uppercase block mb-2">{f.label}</label>
                    <input type="text" value={sig[f.key] || ''} onChange={(e) => setEmailSignatures({
                      ...emailSignatures, company_signature: {...sig, [f.key]: e.target.value}
                    })} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-sm focus:border-[#D4AF37] outline-none" />
                  </div>
                ))}
              </div>

              {/* Social Media */}
              <div className="pt-4 border-t border-white/10">
                <label className="text-white/50 text-xs font-mono uppercase block mb-3">Social Media Links</label>
                <div className="grid grid-cols-2 gap-4">
                  {SOCIAL_FIELDS.map(s => (
                    <div key={s.key}>
                      <label className="text-white/50 text-xs font-mono uppercase block mb-1 flex items-center gap-1.5">
                        <s.icon className="w-3 h-3" /> {s.label}
                      </label>
                      <input type="url" value={socialLinks[s.key] || ''} onChange={(e) => setEmailSignatures({
                        ...emailSignatures, company_signature: {...sig, social_links: {...socialLinks, [s.key]: e.target.value}}
                      })} placeholder={s.placeholder} data-testid={`company-sig-social-${s.key}`}
                        className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-sm focus:border-[#D4AF37] outline-none" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Awards */}
              <div className="pt-4 border-t border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-white/50 text-xs font-mono uppercase">Awards & Certifications</label>
                  <button onClick={async () => {
                    const name = prompt('Award name:');
                    const image_url = prompt('Award image URL:');
                    if (name && image_url) {
                      try { await authAxios.post('/admin/email-signatures/company/award', { name, image_url }); fetchEmailSignatures(); toast.success('Award added!'); }
                      catch { toast.error('Failed'); }
                    }
                  }} className="text-[#D4AF37] text-xs font-mono hover:underline">+ Add Award</button>
                </div>
                <div className="flex flex-wrap gap-3">
                  {sig.awards?.map((a, i) => (
                    <div key={i} className="relative group bg-[#0A0A0A] border border-white/10 p-2 rounded">
                      <img src={a.image_url} alt={a.name} className="h-10 w-auto" />
                      <button onClick={async () => {
                        try { await authAxios.delete(`/admin/email-signatures/company/award/${a.id}`); fetchEmailSignatures(); toast.success('Removed'); }
                        catch { toast.error('Failed'); }
                      }} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100">x</button>
                      <p className="text-white/50 text-[10px] mt-1 text-center">{a.name}</p>
                    </div>
                  ))}
                  {(!sig.awards || sig.awards.length === 0) && <p className="text-white/30 text-xs">No awards yet</p>}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button onClick={handleSaveCompany} disabled={saving} data-testid="save-company-signature"
                  className="flex-1 px-4 py-3 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save Company Signature'}
                </button>
                <button onClick={() => setEditingCompany(false)}
                  className="px-4 py-3 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== ADD/EDIT TEAM SIGNATURE MODAL ===== */}
      {(showAddTeamSig || editingTeamSig) && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111111] border border-white/10 p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-mono font-bold text-lg">{editingTeamSig ? `Edit: ${editingTeamSig.name}` : 'Add Team Signature'}</h3>
              <button onClick={() => { setEditingTeamSig(null); setShowAddTeamSig(false); }} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              {[
                { key: 'name', label: 'Name *', type: 'text', ph: 'John Smith' },
                { key: 'title', label: 'Title *', type: 'text', ph: 'Managing Director' },
                { key: 'email', label: 'Email *', type: 'email', ph: 'john@aureate-resident.com' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">{f.label}</label>
                  <input type={f.type} placeholder={f.ph}
                    value={editingTeamSig ? editingTeamSig[f.key] : newTeamSig[f.key]}
                    onChange={(e) => editingTeamSig ? setEditingTeamSig({...editingTeamSig, [f.key]: e.target.value}) : setNewTeamSig({...newTeamSig, [f.key]: e.target.value})}
                    className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-sm focus:border-[#D4AF37] outline-none" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Phone</label>
                  <input type="text" placeholder="+44 131 381 1989"
                    value={editingTeamSig ? editingTeamSig.phone : newTeamSig.phone}
                    onChange={(e) => editingTeamSig ? setEditingTeamSig({...editingTeamSig, phone: e.target.value}) : setNewTeamSig({...newTeamSig, phone: e.target.value})}
                    className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-sm focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Mobile</label>
                  <input type="text" placeholder="+44 7759 342411"
                    value={editingTeamSig ? editingTeamSig.mobile : newTeamSig.mobile}
                    onChange={(e) => editingTeamSig ? setEditingTeamSig({...editingTeamSig, mobile: e.target.value}) : setNewTeamSig({...newTeamSig, mobile: e.target.value})}
                    className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-sm focus:border-[#D4AF37] outline-none" />
                </div>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">LinkedIn URL</label>
                <input type="text" placeholder="https://linkedin.com/in/..."
                  value={editingTeamSig ? editingTeamSig.linkedin_url : newTeamSig.linkedin_url}
                  onChange={(e) => editingTeamSig ? setEditingTeamSig({...editingTeamSig, linkedin_url: e.target.value}) : setNewTeamSig({...newTeamSig, linkedin_url: e.target.value})}
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-sm focus:border-[#D4AF37] outline-none" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Photo URL</label>
                <input type="text" placeholder="https://..."
                  value={editingTeamSig ? editingTeamSig.photo_url : newTeamSig.photo_url}
                  onChange={(e) => editingTeamSig ? setEditingTeamSig({...editingTeamSig, photo_url: e.target.value}) : setNewTeamSig({...newTeamSig, photo_url: e.target.value})}
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-sm focus:border-[#D4AF37] outline-none" />
                <p className="text-white/30 text-xs mt-1">Leave empty to use company logo</p>
              </div>
              <div className="p-3 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded">
                <p className="text-[#D4AF37]/80 text-xs font-mono">Address, awards & social links inherit from company signature.</p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={async () => {
                const s = editingTeamSig || newTeamSig;
                if (!s.name || !s.title || !s.email) { toast.error('Name, title and email required'); return; }
                try {
                  if (editingTeamSig) { await authAxios.put(`/admin/email-signatures/team/${editingTeamSig.id}`, s); toast.success('Updated!'); }
                  else { await authAxios.post('/admin/email-signatures/team', s); toast.success('Added!'); }
                  fetchEmailSignatures(); setEditingTeamSig(null); setShowAddTeamSig(false);
                  setNewTeamSig({name: '', title: '', phone: '', mobile: '', email: '', linkedin_url: '', photo_url: ''});
                } catch { toast.error('Failed to save'); }
              }} data-testid="save-team-signature"
                className="flex-1 px-4 py-3 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90">
                {editingTeamSig ? 'Update' : 'Add Signature'}
              </button>
              <button onClick={() => { setEditingTeamSig(null); setShowAddTeamSig(false); }}
                className="px-4 py-3 border border-white/20 text-white font-mono text-sm hover:bg-white/5">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SEND TEST MODAL ===== */}
      {sendTestModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111111] border border-white/10 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-mono font-bold flex items-center gap-2"><Send className="w-5 h-5 text-[#D4AF37]" /> Send Test</h3>
              <button onClick={() => { setSendTestModal(null); setTestEmail(''); }} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="p-3 bg-[#0A0A0A] border border-white/10 rounded">
                <p className="text-white/50 text-xs font-mono uppercase mb-1">Signature</p>
                <p className="text-white font-mono text-sm">{sendTestModal.sigName}</p>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Recipient Email</label>
                <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="your@email.com" autoFocus data-testid="send-test-email-input"
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
              </div>
              <div className="flex gap-3">
                <button onClick={handleSendTest} disabled={!testEmail || sending} data-testid="send-test-email-confirm"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90 disabled:opacity-50">
                  <Send className="w-4 h-4" />{sending ? 'Sending...' : 'Send'}
                </button>
                <button onClick={() => { setSendTestModal(null); setTestEmail(''); }}
                  className="px-4 py-3 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailSignaturesTab;
